/**
 * FreedrIQRankings — /freedriq/rankings
 *
 * Public marketing/social-proof page showing recent Community Challenges with
 * their podium and top-3 shareable cards. Anyone can view it; a "Try FreedrIQ"
 * CTA leads unauthenticated visitors to sign up.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import ToolSelector from '../components/ToolSelector';
import { Trophy, Sparkles, Users, ArrowRight, Award, Loader2 } from 'lucide-react';

const MEDAL = {
  1: { color: 'bg-amber-500 text-white',   label: 'Gold'   },
  2: { color: 'bg-slate-300 text-slate-800', label: 'Silver' },
  3: { color: 'bg-amber-700 text-white',   label: 'Bronze' },
};

const FreedrIQRankings = () => {
  const { API, user } = useContext(AuthContext);
  const [rows, setRows] = useState(null);

  useEffect(() => {
    axios.get(`${API}/freedriq/challenge/recent?limit=8`)
      .then((r) => setRows(r.data?.challenges || []))
      .catch(() => setRows([]));
  }, [API]);

  return (
    <div className="min-h-screen bg-background" data-testid="freedriq-rankings-page">
      <ToolSelector />

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent pointer-events-none" />
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-14 relative">
          <div className="eyebrow text-primary inline-flex items-center gap-1.5">
            <Trophy className="w-3 h-3" /> FreezeDryIQ · Community Rankings
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold mt-2 leading-tight">
            The operators topping the FreezeDryIQ leaderboard
          </h1>
          <p className="text-base text-muted-foreground mt-3 leading-relaxed max-w-2xl">
            Every so often, we open the whole 125-question test to every learner on the platform.
            One score, one leaderboard, bragging rights on the line. Here&apos;s who&apos;s been crushing it.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <Link
              to={user ? '/freedriq/challenge' : '/register'}
              className="btn-primary inline-flex items-center gap-1.5 rounded-full font-bold text-sm px-5 py-2.5"
              data-testid="rankings-cta-take"
            >
              {user ? 'Take the next challenge' : 'Join & take the challenge'} <Sparkles className="w-3.5 h-3.5" />
            </Link>
            <Link to="/freedriq?tier=free&phase=pre" className="text-sm font-bold text-primary hover:underline">
              Or start with the free entry test
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
        {rows === null ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm py-16 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading rankings…
          </div>
        ) : rows.length === 0 ? (
          <div className="pro-card p-8 text-center" data-testid="rankings-empty">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-primary/10 text-primary mb-4">
              <Trophy className="w-6 h-6" />
            </div>
            <h2 className="font-heading text-2xl font-extrabold">No challenges have been run yet</h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              Check back once an admin opens the first Community Challenge — this page will
              light up with rankings and winner cards.
            </p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="rankings-list">
            {rows.map((c) => (
              <ChallengeCard key={c.id} challenge={c} API={API} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ChallengeCard = ({ challenge, API }) => {
  const podium = challenge.podium || [];
  const first = podium.find((p) => p.rank === 1);
  const shareCardUrl = first ? `${API}/freedriq/challenge/share-card/${first.submission_id}.png` : null;

  return (
    <div className="pro-card p-5 md:p-6 flex flex-col md:flex-row gap-6" data-testid={`rankings-card-${challenge.id}`}>
      <div className="md:w-1/2 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${
            challenge.is_open ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
          }`}>
            {challenge.is_open ? 'LIVE' : 'CLOSED'}
          </span>
          <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">
            opened {challenge.opened_at?.slice(0, 10)}
          </div>
        </div>
        <h3 className="font-heading text-2xl font-extrabold mt-2 leading-tight">{challenge.name}</h3>
        {challenge.description && (
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{challenge.description}</p>
        )}
        <div className="flex flex-wrap items-center gap-3 mt-3 text-xs">
          <span className="inline-flex items-center gap-1 font-bold">
            <Users className="w-3 h-3" /> {challenge.total_participants} operator{challenge.total_participants === 1 ? '' : 's'}
          </span>
          {challenge.is_open && (
            <Link
              to="/freedriq/challenge"
              className="inline-flex items-center gap-1 text-primary font-bold hover:underline"
              data-testid={`rankings-join-${challenge.id}`}
            >
              Join this challenge <ArrowRight className="w-3 h-3" />
            </Link>
          )}
        </div>

        {podium.length > 0 ? (
          <div className="mt-5 space-y-2">
            <div className="eyebrow text-primary inline-flex items-center gap-1.5">
              <Award className="w-3 h-3" /> Podium
            </div>
            {podium.map((p) => (
              <div key={p.user_id} className="flex items-center gap-3 rounded-lg border border-border p-2.5" data-testid={`rankings-podium-${challenge.id}-${p.rank}`}>
                <div className={`h-8 w-8 rounded-full flex items-center justify-center font-heading font-extrabold text-sm ${MEDAL[p.rank]?.color || 'bg-muted'}`}>
                  {p.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-extrabold text-sm truncate">{p.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{p.role || 'free'}</div>
                </div>
                <div className="font-heading font-extrabold text-lg">{p.score}</div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic mt-4">
            No submissions yet — be the first!
          </p>
        )}
      </div>

      <div className="md:w-1/2 flex items-center justify-center">
        {shareCardUrl ? (
          <img
            src={shareCardUrl}
            alt={`Winner card for ${challenge.name}`}
            className="rounded-lg border border-border w-full max-w-md"
            loading="lazy"
            data-testid={`rankings-winner-card-${challenge.id}`}
          />
        ) : (
          <div className="rounded-lg border-2 border-dashed border-border w-full aspect-[1200/630] flex items-center justify-center text-muted-foreground text-xs">
            No winner card yet
          </div>
        )}
      </div>
    </div>
  );
};

export default FreedrIQRankings;
